import React from "react";
import { Typography, Card, Space } from "antd";
import { Link } from "react-router-dom";

const { Title, Paragraph, Text } = Typography;

const Privacy: React.FC = () => {
  return (
    <div
      style={{
        maxWidth: "800px",
        margin: "0 auto",
        padding: "2rem",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Card>
        <Title level={1}>Privacy Policy</Title>
        <Text type="secondary">
          Last updated: {new Date().toLocaleDateString()}
        </Text>

        <div style={{ marginTop: "2rem" }}>
          <Title level={2}>Introduction</Title>
          <Paragraph>
            At Expedient AI, Inc. ("we," "our," or "us"), we respect your
            privacy and are committed to protecting your personal data. This
            Privacy Policy explains how we collect, use, disclose, and safeguard
            your information when you use our agricultural intelligence platform
            and related services.
          </Paragraph>

          <Title level={2}>Information We Collect</Title>
          <Title level={3}>Personal Information</Title>
          <Paragraph>
            We may collect the following types of personal information:
          </Paragraph>
          <ul>
            <li>
              <strong>Account Information:</strong> Name, email address,
              password, and profile information
            </li>
            <li>
              <strong>Contact Information:</strong> Phone number, mailing
              address, and billing information
            </li>
            <li>
              <strong>Agricultural Data:</strong> Farm location, crop data, soil
              information, weather data, and equipment information
            </li>
            <li>
              <strong>Usage Data:</strong> How you interact with our platform,
              features used, and time spent
            </li>
            <li>
              <strong>Device Information:</strong> IP address, browser type,
              operating system, and device identifiers
            </li>
          </ul>

          <Title level={3}>Automatically Collected Information</Title>
          <Paragraph>
            We automatically collect certain information when you use our
            services:
          </Paragraph>
          <ul>
            <li>Log data and analytics information</li>
            <li>Cookies and similar tracking technologies</li>
            <li>Location data (with your permission)</li>
            <li>Performance and usage metrics</li>
          </ul>

          <Title level={2}>How We Use Your Information</Title>
          <Paragraph>
            We use your information for the following purposes:
          </Paragraph>
          <ul>
            <li>
              <strong>Service Provision:</strong> To provide, maintain, and
              improve our agricultural intelligence services
            </li>
            <li>
              <strong>Personalization:</strong> To customize your experience and
              provide relevant insights
            </li>
            <li>
              <strong>Communication:</strong> To send you updates,
              notifications, and customer support
            </li>
            <li>
              <strong>Analytics:</strong> To analyze usage patterns and improve
              our platform
            </li>
            <li>
              <strong>Security:</strong> To protect against fraud, abuse, and
              security threats
            </li>
            <li>
              <strong>Legal Compliance:</strong> To comply with legal
              obligations and protect our rights
            </li>
          </ul>

          <Title level={2}>Information Sharing and Disclosure</Title>
          <Paragraph>
            We do not sell your personal information. We may share your
            information in the following circumstances:
          </Paragraph>
          <ul>
            <li>
              <strong>Service Providers:</strong> With trusted third-party
              vendors who help us operate our services
            </li>
            <li>
              <strong>Business Partners:</strong> With agricultural partners for
              integrated services (with your consent)
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law or to
              protect our rights and safety
            </li>
            <li>
              <strong>Business Transfers:</strong> In connection with mergers,
              acquisitions, or asset sales
            </li>
            <li>
              <strong>Aggregated Data:</strong> Anonymized, aggregated data for
              research and industry insights
            </li>
          </ul>

          <Title level={2}>Data Security</Title>
          <Paragraph>
            We implement appropriate technical and organizational measures to
            protect your personal information:
          </Paragraph>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security assessments and audits</li>
            <li>Access controls and authentication measures</li>
            <li>Employee training on data protection</li>
            <li>Incident response and breach notification procedures</li>
          </ul>

          <Title level={2}>Your Rights and Choices</Title>
          <Paragraph>
            Depending on your location, you may have the following rights:
          </Paragraph>
          <ul>
            <li>
              <strong>Access:</strong> Request access to your personal
              information
            </li>
            <li>
              <strong>Correction:</strong> Request correction of inaccurate
              information
            </li>
            <li>
              <strong>Deletion:</strong> Request deletion of your personal
              information
            </li>
            <li>
              <strong>Portability:</strong> Request a copy of your data in a
              portable format
            </li>
            <li>
              <strong>Opt-out:</strong> Opt out of certain data processing
              activities
            </li>
            <li>
              <strong>Withdraw Consent:</strong> Withdraw consent for data
              processing
            </li>
          </ul>

          <Title level={2}>Cookies and Tracking Technologies</Title>
          <Paragraph>
            We use cookies and similar technologies to enhance your experience:
          </Paragraph>
          <ul>
            <li>
              <strong>Essential Cookies:</strong> Required for basic
              functionality
            </li>
            <li>
              <strong>Analytics Cookies:</strong> Help us understand how you use
              our services
            </li>
            <li>
              <strong>Preference Cookies:</strong> Remember your settings and
              preferences
            </li>
            <li>
              <strong>Marketing Cookies:</strong> Deliver relevant
              advertisements (with consent)
            </li>
          </ul>

          <Title level={2}>International Data Transfers</Title>
          <Paragraph>
            Your information may be transferred to and processed in countries
            other than your own. We ensure appropriate safeguards are in place
            for such transfers, including:
          </Paragraph>
          <ul>
            <li>Standard contractual clauses</li>
            <li>Adequacy decisions by relevant authorities</li>
            <li>Certification schemes and codes of conduct</li>
          </ul>

          <Title level={2}>Data Retention</Title>
          <Paragraph>
            We retain your personal information for as long as necessary to:
          </Paragraph>
          <ul>
            <li>Provide our services to you</li>
            <li>Comply with legal obligations</li>
            <li>Resolve disputes and enforce agreements</li>
            <li>Protect our legitimate interests</li>
          </ul>
          <Paragraph>
            We will delete or anonymize your information when it is no longer
            needed for these purposes.
          </Paragraph>

          <Title level={2}>Children's Privacy</Title>
          <Paragraph>
            Our services are not intended for children under 13 years of age. We
            do not knowingly collect personal information from children under
            13. If you believe we have collected information from a child under
            13, please contact us immediately.
          </Paragraph>

          <Title level={2}>Changes to This Privacy Policy</Title>
          <Paragraph>
            We may update this Privacy Policy from time to time. We will notify
            you of any material changes by:
          </Paragraph>
          <ul>
            <li>Posting the updated policy on our website</li>
            <li>Sending you an email notification</li>
            <li>Providing notice through our platform</li>
          </ul>

          <Title level={2}>Contact Us</Title>
          <Paragraph>
            If you have any questions about this Privacy Policy or our data
            practices, please contact us:
          </Paragraph>
          <ul>
            <li>
              <strong>Email:</strong> privacy@expedient.one
            </li>
            <li>
              <strong>Address:</strong> Expedient AI, Inc., [Your Address]
            </li>
            <li>
              <strong>Phone:</strong> [Your Phone Number]
            </li>
          </ul>

          <div
            style={{
              marginTop: "3rem",
              padding: "1rem",
              backgroundColor: "#f0f2f5",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <Space direction="vertical" size="small">
              <Text strong>Need help or have questions?</Text>
              <Space split={<Text type="secondary">•</Text>}>
                <Link to="/contact">Contact Support</Link>
                <Link to="/terms">Terms of Service</Link>
                <Link to="/">Back to Home</Link>
              </Space>
            </Space>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Privacy;
